// src/components/all-transactions/AllTransactionsTable.jsx
import React from "react";
import { Paper, Typography, useTheme } from "@mui/material";
import { 
  DataGrid, 
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarExport,
} from "@mui/x-data-grid";


// ✅ Custom Toolbar with buttons on the right
const CustomToolbar = ({ exportFileName }) => {
  return (
    <GridToolbarContainer sx={{ padding: "12px 16px", gap: 1, justifyContent: "flex-end" }}>
      <GridToolbarColumnsButton />
      <GridToolbarExport
        csvOptions={{
          fileName: exportFileName,
          delimiter: ",",
          utf8WithBom: true,
        }}
        printOptions={{
          hideFooter: true,
          hideToolbar: true,
        }}
      />
    </GridToolbarContainer>
  );
};


const AllTransactionsTable = ({ transactions = [], isMobile }) => {
  const theme = useTheme();


  // ✅ Add safety check
  if (!transactions || transactions.length === 0) {
    return (
      <Paper sx={{ p: 4, textAlign: "center", borderRadius: 2 }}>
        <Typography color="text.secondary">No transactions found</Typography>
      </Paper>
    );
  }


  const columns = [
    {
      field: "transactionId",
      headerName: "Transaction ID",
      width: 140,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight={500}>
          {params.value}
        </Typography>
      ),
    },
    {
      field: "userId",
      headerName: "Customer ID",
      width: 110,
    },
    {
      field: "date",
      headerName: "Date",
      width: 120,
    },
    {
      field: "timestamp",
      headerName: "Time",
      width: 90,
    },
    {
      field: "amount",
      headerName: "Amount",
      width: 100,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight={500}>
          ₹{params.value?.toFixed(2) || "0.00"}
        </Typography>
      ),
    },
    {
      field: "platformFee",
      headerName: "Platform Fee",
      width: 100,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight={500}>
          ₹{params.value?.toFixed(2) || "0.00"}
        </Typography>
      ),
    },
    {
      field: "transactionFee",
      headerName: "Trans. Fee",
      width: 95,
      renderCell: (params) => (
        <Typography variant="body2" fontWeight={500}>
          ₹{params.value?.toFixed(2) || "0.00"}
        </Typography>
      ),
    },
    // ✅ NEW: Penalty Column
    {
      field: "rejectionPenalty",
      headerName: "Penalty",
      width: 90,
      renderCell: (params) => {
        const penalty = params.value || 0;
        return penalty > 0 ? (
          <Typography variant="body2" fontWeight={600} color="error.main">
            -₹{penalty.toFixed(2)}
          </Typography>
        ) : (
          <Typography variant="body2" color="text.disabled">
            -
          </Typography>
        );
      },
    },
    {
      field: "netEarning",
      headerName: "Net Earning",
      width: 110,
      renderCell: (params) => {
        const row = params.row;
        const isRejected = row.status === "REJECTED";
        return (
          <Typography 
            variant="body2" 
            fontWeight={500} 
            color={isRejected ? "text.disabled" : "success.main"} // ✅ Gray for rejected
          >
            ₹{params.value?.toFixed(2) || "0.00"}
          </Typography>
        );
      },
    },
    {
      field: "status",
      headerName: "Status",
      width: 100,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Typography
          variant="body2"
          fontWeight={500}
          sx={{
            color:
              params.value === "COMPLETED"
                ? "success.main"
                : params.value === "REJECTED" // ✅ Changed from FAILED
                ? "error.main"
                : "warning.main",
          }}
        >
          {params.value}
        </Typography>
      ),
    },
  ];


  // Generate filename with current date
  const exportFileName = `transactions-${new Date().toISOString().split('T')[0]}`;


  return (
    <Paper 
      sx={{ 
        borderRadius: 2, 
        overflow: "hidden",
        mb: isMobile ? 2 : 0,
        height: 400, // ✅ Fixed height in pixels
      }}
    >
      <DataGrid
        rows={transactions}
        columns={columns}
        checkboxSelection={false}
        disableSelectionOnClick
        disableRowSelectionOnClick
        autoHeight={false} // ✅ Critical: must be false
        slots={{
          toolbar: () => <CustomToolbar exportFileName={exportFileName} />,
        }}
        sx={{
          border: "none",
          height: "100%", // ✅ Fill the Paper container
          "& .MuiDataGrid-cell": {
            fontSize: "0.875rem",
            display: "flex",
            alignItems: "center",
          },
          "& .MuiDataGrid-columnHeaders": {
            bgcolor: theme.palette.mode === "dark" ? "grey.900" : "grey.100",
            fontSize: "0.875rem",
            fontWeight: 500,
            borderBottom: `2px solid ${theme.palette.divider}`,
          },
          "& .MuiDataGrid-row": {
            minHeight: "52px !important",
            maxHeight: "52px !important",
            // ✅ Highlight rejected rows
            "&.rejected-row": {
              bgcolor: theme.palette.mode === "dark" 
                ? "rgba(211, 47, 47, 0.08)" 
                : "rgba(211, 47, 47, 0.04)",
            },
          },
          "& .MuiDataGrid-row:hover": {
            bgcolor: theme.palette.action.hover,
          },
          "& .MuiDataGrid-footerContainer": {
            borderTop: `2px solid ${theme.palette.divider}`,
          },
          // ✅ Toolbar styling - buttons on right
          "& .MuiDataGrid-toolbarContainer": {
            padding: "12px 16px",
            gap: 1,
            borderBottom: `1px solid ${theme.palette.divider}`,
          },
        }}
        // ✅ Apply custom class to rejected rows
        getRowClassName={(params) => 
          params.row.status === "REJECTED" ? "rejected-row" : ""
        }
        initialState={{
          pagination: {
            paginationModel: { pageSize: 25 },
          },
        }}
        pageSizeOptions={[10, 25, 50, 100]}
      />
    </Paper>
  );
};


export default AllTransactionsTable;
